import * as React from 'react';

import { Stack, FormControl, InputLabel, Input, FormLabel, RadioGroup, FormControlLabel, Radio, Checkbox, TextField } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { AlertProps } from '@mui/material/Alert';
import { WSProdFormProps } from '../WSTesting'
import { WorkshopProductCombination, WSProdConfigAllReady, setAllProdTestCombination } from '../../types/WorkshopProductCombination';
import { TestSupportStatus } from '../../types/WorkshopTestCases';
import { insertresultheaders } from '../../types/WorkshopResult';
import { TestCombination } from '../../types/TestCombinations';
import { useSnackbar } from '../../../contexts/SnackBarContext';
import { ButtonLink } from '../../subs/ButtonLink';
import { copyArrayWithIndexChanged } from '../../Utils';
import { httpPost } from '../../Web';

export const WorkshopProductForm = ({ selectedTreeType, workshopProduct, workshopInterfaces, product, productInterfaces, interfaces, disabled,
    wsProdCombination, wptcUpdateHandler, wsProdCombinations, testCombinations, testCases, workshopTestcases, isGlobalAdminUser, wpSubmitHandler, wpIfaceSubmitHandler, completionFlagSetter }:
    WSProdFormProps &
    {
        isGlobalAdminUser?: boolean,
        testCombinations?: TestCombination[],
        wsProdCombination: WorkshopProductCombination,
        wsProdCombinations?: WorkshopProductCombination[],
        wptcUpdateHandler?: (wspCombination: WorkshopProductCombination, wspCombinations: WorkshopProductCombination[]) => void,
        completionFlagSetter?: (value: React.SetStateAction<boolean>) => void
    }) => {
    const { showSnackbar } = useSnackbar();
    const [wp, setWp] = React.useState(workshopProduct);
    const [wptc, setWpTc] = React.useState(wsProdCombination);
    const [wptclistList, setWpTcList] = React.useState(wsProdCombinations);
    const [prod, setProd] = React.useState(product);
    const [wpIfaces, setWpIfaces] = React.useState(productInterfaces);
    const [allReady, setAllReady] = React.useState<string | undefined>(undefined);

    const showUserFeedback = React.useCallback((msg: string | null, severity: AlertProps['severity'] = 'error') => {
        msg && showSnackbar(msg, severity);
    }, [showSnackbar]);

    React.useEffect(() => {
        setProd(product);
    }, [product]);

    React.useEffect(() => {
        if (allReady === undefined) return;     // if the user makes a selection MUI will force the radio to change. Here we want to force MUI to empty the control again.
        setAllReady(undefined);
    }, [allReady]);

    React.useEffect(() => {
        setWp(workshopProduct);
    }, [workshopProduct]);

    React.useEffect(() => {
        setWpTc(wsProdCombination);
    }, [wsProdCombination]);

    React.useEffect(() => {
        setWpTcList(wsProdCombinations);
    }, [wsProdCombinations]);

    React.useEffect(() => {
        setWpIfaces(productInterfaces);
    }, [productInterfaces]);

    React.useEffect(() => {
        completionFlagSetter?.(Boolean(wp.productVersion?.trim() &&
            ((wptc.providerReadyToTest && selectedTreeType === 'P') || (wptc.consumerReadyToTest && selectedTreeType === 'C'))
                && wpIfaces?.some(wpIface => wpIface.supportedByProduct)));
    }, [wp, wptc, wpIfaces, completionFlagSetter, selectedTreeType]);

    const OnReadyToTestChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.value === 'true') {
            const wstcases = workshopTestcases?.filter(wstc => wstc.supportStatus !== TestSupportStatus.Undecided);
            const wstcCount = wstcases?.length ?? 0;
            let testCaseCount: number = 0;
            if (testCases !== undefined) testCases.forEach((tc, key) => productInterfaces?.find(pi => pi.interfaceId === key)?.supportedByProduct ? testCaseCount += tc?.length ?? 0 : 0);
            if (wstcCount < testCaseCount) {
                showUserFeedback('Please mark all test cases as supported or unsupported, before making the Product ready for testing.');
                setWpTc({ ...wptc, providerReadyToTest: false, consumerReadyToTest: false });
                //     wpSubmitHandler({ ...workshopProduct, serverReadyToTest: false, clientReadyToTest: false });
                return;
            }
        }
        else if (event.target.value === 'reset') {
            insertResultHeadersHandler();
            return;
        }

        if (selectedTreeType === 'P' && wsProdCombination.isProvider) {
            setWpTc({ ...wptc, providerReadyToTest: event.target.value === 'true' });
        }
        if (selectedTreeType === 'C' && wsProdCombination.isConsumer) {
            setWpTc({ ...wptc, consumerReadyToTest: event.target.value === 'true' });
        }
    }

    const OnReadyToTestChangeAllHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        let wsProdConfigAllReady: WSProdConfigAllReady = {
            workshopProductId: wp.id ?? 0,
            setReadyToTest: event.target.value === 'true'
        }
        setAllReady(event.target.value);        // set the value to trigger the useEffect to reset the radio button again
        
        setAllReadyToTest(wsProdConfigAllReady);
    }

    const setAllReadyToTest = async (wsProdConfigAllReady: WSProdConfigAllReady) => {
        if (wptcUpdateHandler === undefined || wptcUpdateHandler === null) return;

        const response = await setAllProdTestCombination(wsProdConfigAllReady, new AbortController());

        if (response.failed) {
            showUserFeedback(response.errorText);
            return;
        }

        const updatedCombinations = response.result;
        let wptclistListLoc = wptclistList;
        let wptcLoc = wptc;
        let errorNotice: string = '';

        updatedCombinations.forEach(updatedCombination => {
            if (wsProdCombination.id === updatedCombination.id) {
                wptcLoc = updatedCombination;
                setWpTc(wptcLoc);
            }
            if (wptclistListLoc !== undefined) {
                wptclistListLoc = copyArrayWithIndexChanged(wptclistListLoc ?? [], wptclistListLoc?.findIndex(wpc => wpc.id === updatedCombination.id) ?? -1, updatedCombination);
            }
            if (updatedCombination.updateErrorReason !== undefined && updatedCombination.updateErrorReason !== null && updatedCombination.updateErrorReason !== '') {
                errorNotice += `Combination ${testCombinations?.find(tc => tc.id === updatedCombination.testCombinationId)?.combinationName ?? updatedCombination.testCombinationId}: ${updatedCombination.updateErrorReason}\n`;
            }
        });

        if (wptclistListLoc !== undefined) setWpTcList(wptclistListLoc);
        if (wptcUpdateHandler) wptcUpdateHandler(wptcLoc, wptclistListLoc ?? []);

        if (errorNotice !== '') showUserFeedback(errorNotice);
    }

    // rebuild the result headers if the counters are wrong...
    const insertResultHeadersHandler = async () => {
        // insert the Results header for this workshop product and all combinations of interfaces, if it is set to ready to test
        const results = insertresultheaders(wptc, new AbortController());

        // but if an error occurs, we can show it to the user
        results.then((response) => {
            if (response.failed) showUserFeedback(response.errorText);
        }).catch((response) => {
            showUserFeedback(response);
        });
    }

    const onSubmitHandler = (e: { preventDefault: () => void; }) => {
        e.preventDefault();

        // only submit changes by the user
        if (JSON.stringify(workshopProduct) !== JSON.stringify(wp) || JSON.stringify(wsProdCombination) !== JSON.stringify(wptc))
            wpSubmitHandler(wp, wptc);

       wpIfaceSubmitHandler(wpIfaces);
    }
    //{/*onChange={(event) => setProd({ ...prod, name: event.target.value })*/}
    return <>
        <form id='productForm' onSubmit={onSubmitHandler}>
            <fieldset disabled={disabled} className={'disabledForm'}>
                <Stack direction={'column'} rowGap={30}>
                    <Grid container rowGap={20} columnSpacing={20}>
                        <Grid size={{ xs: 4 }}>
                            <FormControl required fullWidth>
                                <InputLabel>{"Product name"}</InputLabel>
                                <Input value={prod.name} disabled />
                            </FormControl>
                        </Grid>
                        <Grid size={{ xs: 4 }}>
                            <FormControl required fullWidth>
                                <InputLabel>{"Product version"}</InputLabel>
                                <Input value={wp.productVersion ?? ""} onChange={(event) => setWp({ ...wp, productVersion: event.target.value })} />
                            </FormControl>
                        </Grid>
                        <Grid size={{ xs: 4 }}>
                            {selectedTreeType === 'P' && wptc.isProvider &&
                                <FormControl required>
                                    <FormLabel>{"Provider ready to test?"}</FormLabel>
                                    <RadioGroup name='serverRadioButtons' row value={wptc.providerReadyToTest} onChange={OnReadyToTestChangeHandler}>
                                        <FormControlLabel value={'true'} control={<Radio />} label={"Yes"} />
                                        <FormControlLabel value={'false'} control={<Radio />} label={"No"} />
                                        {isGlobalAdminUser && <FormControlLabel value='reset' control={<Radio />} label={"Recalculate"} />}
                                    </RadioGroup>
                                </FormControl>}

                            {selectedTreeType === 'C' && wptc.isConsumer &&
                                <FormControl required>
                                    <FormLabel>{"Consumer ready to test?"}</FormLabel>
                                    <RadioGroup name='clientRadioButtons' row value={wptc.consumerReadyToTest} onChange={OnReadyToTestChangeHandler}>
                                        <FormControlLabel value='true' control={<Radio />} label={"Yes"} />
                                        <FormControlLabel value='false' control={<Radio />} label={"No"} />
                                        {isGlobalAdminUser && <FormControlLabel value='reset' control={<Radio />} label={"Recalculate"} />}
                                    </RadioGroup>
                                </FormControl>}
                        </Grid>

                        <Grid container size={{ xs: 12 }}>
                            <Grid container size={{ xs: 5 }} >
                                <FormControl required>
                                    <FormLabel>{"Supported Interfaces"}</FormLabel>
                                    {wpIfaces && workshopInterfaces.map(wi => {
                                        const wpIfaceCurrentIndex = wpIfaces.findIndex(wpIface => wpIface.interfaceId === wi.interfaceId);

                                        return <Grid container size={{ xs: 12 }}>
                                            <FormControlLabel control={<Checkbox checked={wpIfaces[wpIfaceCurrentIndex]?.supportedByProduct ? true : false} disabled
                                                /*onChange={checkedOnChangeHandler}*/ />}
                                                label={interfaces.find(iface => iface.id === wi.interfaceId)?.name} />
                                        </Grid>;
                                    })}
                                </FormControl>
                            </Grid>
                            <Grid container size={{ xs: 7 }} >
                                {(disabled || allReady !== undefined) ? null :  // for some reason MUI turns the text white after selection and won'T unset the radio value, this workaround prevents that
                                    <FormControl>
                                        <Grid container rowGap={2} columnSpacing={5}>
                                            <Grid container size={{ xs: 8 }}>
                                                <FormLabel sx={{ alignContent: 'center' }}>{'Set all to Ready:'}</FormLabel>
                                            </Grid>
                                            <Grid container size={{ xs: 4 }}>
                                                    <RadioGroup name={'displayProviderReadyALL'} row onChange={OnReadyToTestChangeAllHandler} value={undefined} >
                                                        <FormControlLabel value={'true'} control={<Radio />} label={"Yes"} />
                                                        <FormControlLabel value={'false'} control={<Radio />} label={"No"} />
                                                    </RadioGroup>
                                            </Grid>
                                            <Grid container size={{ xs: 4 }} borderBottom={1} borderTop={1}>
                                                <FormLabel >{"Scenario"}</FormLabel>
                                            </Grid>
                                            <Grid container size={{ xs: 4 }} borderBottom={1} borderTop={1}>
                                                <FormLabel>{"Consumer Ready"}</FormLabel>
                                            </Grid>
                                            <Grid container size={{ xs: 4 }} borderBottom={1} borderTop={1}>
                                                <FormLabel>{"Provider Ready"}</FormLabel>
                                            </Grid>
                                            {wptclistList && testCombinations && wptclistList.map(wpc => {
                                                const wpcCurrentIndex = testCombinations.findIndex(tc => tc.id === wpc.testCombinationId);

                                                return wpc.workshopProductId === wp.id && wpcCurrentIndex !== -1 ? <>
                                                    <Grid container size={{ xs: 4 }}>
                                                        <FormLabel sx={{ alignContent: 'center' }}>{testCombinations[wpcCurrentIndex].combinationName}</FormLabel>
                                                    </Grid>
                                                    <Grid container size={{ xs: 4 }}>
                                                        {wpc.isConsumer &&
                                                            <RadioGroup name={'displayConsumerReady' + wpc.id} row value={wpc.consumerReadyToTest}>
                                                                <FormControlLabel value={'true'} control={<Radio />} label={"Yes"} disabled />
                                                                <FormControlLabel value={'false'} control={<Radio />} label={"No"} disabled />
                                                            </RadioGroup>}
                                                    </Grid>
                                                    <Grid container size={{ xs: 4 }}>
                                                        {wpc.isProvider &&
                                                            <RadioGroup name={'displayProviderReady' + wpc.id} row value={wpc.providerReadyToTest} >
                                                                <FormControlLabel value={'true'} control={<Radio />} label={"Yes"} disabled />
                                                                <FormControlLabel value={'false'} control={<Radio />} label={"No"} disabled />
                                                            </RadioGroup>}
                                                    </Grid>
                                                </> : null;
                                            })}
                                        </Grid>
                                    </FormControl>
                                }
                            </Grid>
                            <Grid container size={{ xs: 12 }}>
                                <Grid container size={{ xs: 6 }}>
                                    <ButtonLink key={`Start${workshopProduct.workshopId}`} to={`/ViewUserProducts/${workshopProduct.workshopId}`}
                                        /*startIcon={<AutorenewIcon />}*/ >
                                        Edit Products
                                    </ButtonLink>
                                </Grid>
                                <Grid container size={{ xs: 6 }}>
                                    
                                </Grid>
                            </Grid>

                            {selectedTreeType === 'P' && wsProdCombination.isProvider &&
                                <Grid container size={{ xs: 12 }}>
                                    <FormControl fullWidth>
                                        <FormLabel>{"ServerProgID / URL"}</FormLabel>
                                        {wpIfaces && workshopInterfaces.map((wi, index) => {
                                            const wpIfaceCurrentIndex = wpIfaces.findIndex(wpIface => wpIface.interfaceId === wi.interfaceId);
                                            const wpIfaceDefined = wpIfaceCurrentIndex !== -1;

                                            return <Grid height={'100%'}>
                                                {wpIfaceDefined &&
                                                    <TextField size='small' fullWidth
                                                        disabled={!wpIfaces[wpIfaceCurrentIndex].supportedByProduct}
                                                        value={wpIfaces[wpIfaceCurrentIndex]?.serverEndpoint}
                                                        onChange={(e) => {
                                                            const currentWpIface = wpIfaces[wpIfaceCurrentIndex];
                                                            setWpIfaces(copyArrayWithIndexChanged(wpIfaces, wpIfaceCurrentIndex, { ...currentWpIface, serverEndpoint: e.target.value }));
                                                        }} />}
                                            </Grid>
                                        })}
                                    </FormControl>
                                </Grid>}
                        </Grid>

                        <Grid size={{ xs: 12 }}>
                            <FormControl fullWidth>
                                <TextField label={"Product description"} variant='outlined' multiline minRows={7} maxRows={7} fullWidth
                                    onChange={event => setWp({ ...wp, description: event.target.value })} value={wp.description ?? ""} />
                            </FormControl>
                        </Grid>
                    </Grid>
                </Stack>
            </fieldset>
        </form></>
}